import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/profile';

const ROLE_RANK: Record<UserRole, number> = { user: 0, smugler: 1, admin: 2 };

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Minimum role required to access this route. Defaults to any authenticated user. */
  requiredRole?: UserRole;
}

/**
 * Wraps a route to require authentication (and optionally a minimum role).
 * - Not logged in → redirects to /login
 * - Logged in but wrong role → redirects to / with a 403-style replace
 *
 * Usage:
 *   <ProtectedRoute requiredRole="admin"><AdminPanel /></ProtectedRoute>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, role, loading } = useAuth();
  const location = useLocation();

  // Show nothing while auth state is loading to prevent flash of login page
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-neon-green text-lg animate-pulse">Laster...</div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login, preserving the page they tried to visit
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role check: compare rank so 'admin' passes a check for 'smugler'
  if (requiredRole && role !== null) {
    const userRank = ROLE_RANK[role] ?? 0;
    const requiredRank = ROLE_RANK[requiredRole] ?? 0;
    if (userRank < requiredRank) {
      return <Navigate to="/" replace />;
    }
  } else if (requiredRole && role === null) {
    // Role not loaded yet or user has no profile — block access
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
